import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button } from "@mui/material"
import { ALL_LOCALES, LOCALES } from "app/language/model"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { isEmptyObject } from "app/tools/common"
import { FieldPhone } from "form/components/fields/FieldPhone"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { FieldText } from "form/components/fields/FieldText"
import { Form, Formik } from "formik"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { addImportant } from "ui/tools/common"
import { ISignUpUserForm, IUserSignupFormData } from "user/model"
import { incrementSignUpStep, setSignUpUserForm, validateSignupUserFormData } from "user/tools/signup"

interface ISignUpWizardUserFormProps {
  userForm: ISignUpUserForm
  containerWidth: number
}

const SignUpWizardUserFormFn = React.memo(({ userForm, containerWidth }: ISignUpWizardUserFormProps) => {
  const [userData, setUserData] = React.useState<IUserSignupFormData>(userForm.data)
  return (
    <FlexBox fullWidth vertical gap sx={{ maxWidth: "100%", height: "100%", minHeight: "100%", maxHeight: "100%" }}>
      <Formik
        initialValues={userData}
        validate={(values: IUserSignupFormData) => {
          setUserData(values)
          const errors = validateSignupUserFormData(values)
          setSignUpUserForm({
            data: values,
            errors,
            isValid: isEmptyObject(errors)
          })
          return errors
        }}
        onSubmit={async () => incrementSignUpStep()}>
        {({ errors, isSubmitting, values }) => {
          const canSubmit = !isSubmitting && userForm.isValid
          return (
            <Form
              id={"signup-user-form"}
              style={{
                width: "100%",
                padding: ".5rem",
                maxWidth: "100%",
                height: "100%",
                minHeight: "100%",
                maxHeight: "100%"
              }}>
              <FlexBox
                vertical
                gap
                ai="flex-start"
                jc="flex-start"
                sx={{
                  overflow: "auto",
                  width: "100%",
                  minWidth: "100%",
                  maxWidth: "100%",
                  height: "calc(100% - 5rem)",
                  minHeight: "calc(100% - 5rem)",
                  maxHeight: "calc(100% - 5rem)",
                  pt: ".5rem"
                }}>
                <FlexBox
                  wrap
                  gap
                  fullWidth
                  jc="flex-start"
                  vertical={containerWidth < 1030}
                  sx={{
                    "& > *": {
                      width: addImportant(containerWidth > 1030 ? "48%" : "100%")
                    }
                  }}>
                  <FieldText
                    fieldName="email"
                    label={translate("label.email")}
                    value={values.email}
                    error={errors.email}
                    autoFocus
                    required
                  />
                  <FieldText
                    fieldName="firstname"
                    label={translate("label.firstname")}
                    value={values.firstname as string}
                    error={errors.firstname}
                    required
                  />
                  <FieldText
                    fieldName="lastname"
                    label={translate("label.lastname")}
                    value={values.lastname as string}
                    error={errors.lastname}
                    required
                  />
                  <FieldPhone
                    fieldName="phone"
                    required
                    defaultCountry={
                      navigator.language.length === 5 ? navigator.language.substring(3, 5).toLowerCase() : "us"
                    }
                    error={errors.phone}
                    value={values.phone || ""}
                    style={{ zIndex: 10 }}
                  />
                  <FieldText
                    fieldName="password1"
                    label={translate("user.password1.create")}
                    value={values.password1 as string}
                    error={errors.password1}
                    isPassword
                    clearable
                    required
                  />
                  <FieldText
                    fieldName="password2"
                    label={translate("user.password2")}
                    value={values.password2 as string}
                    error={errors.password2}
                    isPassword
                    clearable
                    required
                  />
                  <FieldSelect
                    fieldName="language_id"
                    label={translate("label.language")}
                    value={values.language_id || LOCALES.EN}
                    options={ALL_LOCALES.slice()}
                    optionLabelFn={locale => translate(`label.language.${locale}`)}
                    error={errors.language_id}
                  />
                </FlexBox>
              </FlexBox>
              <FlexBox gap fullWidth ai="end" jc="space-between" sx={{ mt: "2rem", width: "100%" }}>
                <Button
                  id="signup-user-form-button-cancel"
                  variant="outlined"
                  disabled={true}
                  startIcon={<FontAwesomeIcon icon={faChevronLeft} className="icon-previous" />}>
                  {translate("label.step.previous")}
                </Button>
                <Button
                  id="signup-user-form-button-save"
                  variant="contained"
                  endIcon={<FontAwesomeIcon icon={faChevronRight} className="icon-next" />}
                  type="submit"
                  disabled={!canSubmit}>
                  {translate("label.step.next")}
                </Button>
              </FlexBox>
            </Form>
          )
        }}
      </Formik>
    </FlexBox>
  )
})

export const SignUpWizardUserForm = connect(
  (state: IRootState): ISignUpWizardUserFormProps => ({
    containerWidth: state.ui.container.width,
    userForm: state.user.signup.userForm
  })
)(SignUpWizardUserFormFn)
